import { Inject, Injectable } from '@angular/core';
import { map, tap } from 'rxjs/operators';
import { BehaviorSubject, Subject } from 'rxjs';
import { DomSanitizer } from '@angular/platform-browser';

import { StorageService } from '@coin-depo-core/storage';
import { UsersApiService } from '@coin-depo/http';

import { UserTypeService } from './user-type.service';

import type {
  UserDetailsResponse,
  UpdateUserSettings,
  UpdateUserSettingsRequest,
} from '@coin-depo-types';
import { VERIFICATION_FACADE, VerificationFacade } from '@coin-depo-core/verification';
import { AUTH_SERVICE_TOKEN, AuthFacade } from '@coin-depo-core/auth';

@Injectable({
  providedIn: 'root',
})
export class UserDetailsService {
  private userDetails$ = new BehaviorSubject<UserDetailsResponse>(
    this.storage.get('userDetails')
  );
  cachedUserDetails$ = this.userDetails$.asObservable();
  cachedUserAvatar$ = this.cachedUserDetails$.pipe(
    map((userDetails) =>
      userDetails?.avatar
        ? this.sanitizer.bypassSecurityTrustUrl(`${userDetails?.avatar}`)
        : null
    )
  );

  private languageChangedSubject = new Subject();
  languageChanged$ = this.languageChangedSubject.asObservable();

  constructor(
    private storage: StorageService,
    @Inject(VERIFICATION_FACADE) private verificationService: VerificationFacade,
    private usersApiService: UsersApiService,
    @Inject(AUTH_SERVICE_TOKEN) private auth: AuthFacade,
    private userTypeService: UserTypeService,
    private sanitizer: DomSanitizer
  ) {}

  userDetailsSnapshot() {
    return this.userDetails$.value;
  }

  getReferrals(addData: any) {
    const data: any = {
      username: this.auth.userData.username,
      client_token: this.auth.accessToken,
      ...addData,
    };
    return this.usersApiService.getReferrals(data);
  }

  getUserFrozenBonus(dat: any) {
    const data: any = {
      username: this.auth.userData.username,
      client_token: this.auth.accessToken,
      ...dat,
    }
    return this.usersApiService.getUserFrozenBonus(data)
  }

  setLanguage(lang: string = 'en') {
    const data: any = {
      username: this.auth.userData.username,
      client_token: this.auth.accessToken,
      lang,
    };
    return this.usersApiService
      .setLanguage(data)
      .pipe(tap(() => this.languageChangedSubject.next(lang)));
  }

  getAffiliates(addData: any) {
    const data: any = {
      username: this.auth.userData.username,
      client_token: this.auth.accessToken,
      ...addData,
      ref_type: 'affiliate',
    };
    return this.usersApiService.getReferrals(data);
  }

  updateUserDetails() {
    return this.usersApiService
      .getDetails({
        ...this.auth.userData,
        client_token: this.auth.accessToken,
      })
      .pipe(
        tap((response: UserDetailsResponse) => {
          if (localStorage.getItem('language') !== response.language) {
            // @ts-ignore
            window.setLocale(response.language);
          }
          this.userTypeService.setUserType(response.user_type);
          this.verificationService.setIdentityVerification(
            response.sumsub_verification === 'required'
            ? (response.sumsub_status === 'verified' && (response.sumsub_poa !== 'required' || response.sumsub_poa_status === 'verified') )
            : response.status === 'verified'
          );
          this.verificationService.setIsSumsubKYCRequired(response.sumsub_verification === 'required');
          this.verificationService.setSumsubKYC(response.sumsub_status === 'verified');
          this.verificationService.setIsSumsubPOARequired(response.sumsub_poa === 'required');
          this.verificationService.setSumsubPOA(response.sumsub_poa_status === 'verified')

          this.verificationService.setUserDetailsStatus(
            response.status || null
          );
          this.verificationService.setTOTPEnabledVerification(!!response.totp);
          this.setUserDetails(response);
        })
      );
  }

  sendRefCode(refCode: string, ref_email: string, ref_type = 'referral') {
    const data: any = {
      client_token: this.auth.accessToken,
      username: this.auth.userData.username,
      ref_id: refCode,
      ref_type,
      ref_email,
    };
    return this.usersApiService.sendRefCode(data);
  }

  canDeleteUser() {
    return this.usersApiService.canDeleteUser({
      ...this.auth.userData,
      client_token: this.auth.accessToken,
    });
  }

  verificationUserDetails(data: any) {
    return this.usersApiService.updateUserDetails({
      ...this.auth.userData,
      client_token: this.auth.accessToken,
      ...data,
    });
  }

  updateSettings(settings: UpdateUserSettings) {
    const data: UpdateUserSettingsRequest = {
      username: this.auth.userData.username,
      client_token: this.auth.accessToken,
      ...settings,
    };
    return this.usersApiService.updateUserSettings(data);
  }

  setAvatar(avatar: Blob) {
    const data: any = {
      username: this.auth.userData.username,
      client_token: this.auth.accessToken,
      avatar,
    };
    return this.usersApiService.setAvatar(data);
  }

  clearAvatar() {
    const data: any = {
      username: this.auth.userData.username,
      client_token: this.auth.accessToken,
    };
    return this.usersApiService.setAvatar(data);
  }

  private setUserDetails(details: UserDetailsResponse) {
    this.storage.set('userDetails', details);
    this.userDetails$.next(details);
  }

  getKYCToken(flow: 'kyc' | 'poa' = 'kyc' ) {
    const data: any = {
      username: this.auth.userData.username,
      client_token: this.auth.accessToken,
      flow
    };

    return this.usersApiService.getKYCToken(data);
  }
}
